// SettingsPage.js
// Page for editing user and family settings

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import BottomNav from '../components/BottomNav';
import { useAdmin } from '../contexts/AdminContext';

function SettingsPage({ user, userProfile, onSettingsSaved }) {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();

  // User settings
  const [firstName, setFirstName] = useState(userProfile?.firstName || '');

  // Family settings
  const [familyName, setFamilyName] = useState('');
  const [familyData, setFamilyData] = useState(null);
  const [joinCode, setJoinCode] = useState('');

  // Premium/License settings
  const [licenseCode, setLicenseCode] = useState('');
  const [premiumStatus, setPremiumStatus] = useState('loading'); // 'loading' | 'none' | 'active' | 'inactive'
  const [currentLicenseCode, setCurrentLicenseCode] = useState(null);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState(false);

  // UI states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch family data and check premium status on mount
  useEffect(() => {
    const fetchFamilyData = async () => {
      if (userProfile?.familyId) {
        try {
          const familyDoc = await getDoc(doc(db, 'families', userProfile.familyId));
          if (familyDoc.exists()) {
            const data = familyDoc.data();
            setFamilyData(data);
            setFamilyName(data.name || '');
            setJoinCode(data.joinCode || '');
            setCurrentLicenseCode(data.licenseCode || null);

            // Check premium status
            if (data.licenseCode) {
              const purchasesQuery = query(
                collection(db, 'purchases'),
                where('licenseCode', '==', data.licenseCode)
              );
              const snapshot = await getDocs(purchasesQuery);
              
              if (!snapshot.empty) {
                const purchaseData = snapshot.docs[0].data();
                if (purchaseData.status === 'active') {
                  setPremiumStatus('active');
                } else {
                  setPremiumStatus('inactive');
                }
              } else {
                setPremiumStatus('none');
              }
            } else {
              setPremiumStatus('none');
            }
          }
        } catch (err) {
          console.error('Error fetching family data:', err);
          setPremiumStatus('none');
        }
      }
    };

    fetchFamilyData();
  }, [userProfile?.familyId]);

  // Handle claiming a license code
  const handleClaimLicense = async (e) => {
    e.preventDefault();
    setClaimError('');
    setClaimSuccess(false);

    if (!licenseCode.trim()) {
      setClaimError('Please enter a license code');
      return;
    }

    setClaimLoading(true);

    try {
      // Query for the purchase with this license code
      const purchasesQuery = query(
        collection(db, 'purchases'),
        where('licenseCode', '==', licenseCode.trim())
      );
      const snapshot = await getDocs(purchasesQuery);

      if (snapshot.empty) {
        setClaimError('Invalid license code. Please check and try again.');
        setClaimLoading(false);
        return;
      }

      const purchaseDoc = snapshot.docs[0];
      const purchaseData = purchaseDoc.data();

      // Validate the purchase
      if (purchaseData.status !== 'active') {
        setClaimError('This license is no longer active. Please contact support.');
        setClaimLoading(false);
        return;
      }

      // Check if already claimed by another family
      if (purchaseData.claimStatus === 'claimed' && 
          purchaseData.claimedByFamilyId !== userProfile.familyId) {
        setClaimError('This license has already been claimed by another family.');
        setClaimLoading(false);
        return;
      }

      // Claim the license using a transaction
      await runTransaction(db, async (transaction) => {
        // Update the purchase document
        transaction.update(doc(db, 'purchases', purchaseDoc.id), {
          claimStatus: 'claimed',
          claimedByFamilyId: userProfile.familyId,
          claimedAt: new Date()
        });

        // Update the family document
        transaction.update(doc(db, 'families', userProfile.familyId), {
          licenseCode: licenseCode.trim()
        });
      });

      // Success!
      setClaimSuccess(true);
      setCurrentLicenseCode(licenseCode.trim());
      setPremiumStatus('active');
      setLicenseCode('');

      // Refresh family data
      if (onSettingsSaved) {
        await onSettingsSaved();
      }
    } catch (err) {
      console.error('Error claiming license:', err);
      setClaimError('Failed to claim license. Please try again.');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    // Validation
    if (!firstName.trim()) {
      setError('First name is required');
      setLoading(false);
      return;
    }

    if (!familyName.trim()) {
      setError('Family name is required');
      setLoading(false);
      return;
    }

    try {
      // Update user's firstName
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: firstName.trim()
      });

      // Update family name
      if (userProfile?.familyId) {
        await updateDoc(doc(db, 'families', userProfile.familyId), {
          name: familyName.trim()
        });

        // Also update memberNames in family document if user's name changed
        if (familyData?.memberNames && familyData.memberNames[user.uid] !== firstName.trim()) {
          const updatedMemberNames = {
            ...familyData.memberNames,
            [user.uid]: firstName.trim()
          };
          await updateDoc(doc(db, 'families', userProfile.familyId), {
            memberNames: updatedMemberNames
          });
        }
      }

      // Refresh user profile in App.js
      if (onSettingsSaved) {
        await onSettingsSaved();
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Function to copy join code to clipboard
  const copyJoinCode = () => {
    if (joinCode) {
      navigator.clipboard.writeText(joinCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Render premium status badge
  const renderPremiumBadge = () => {
    switch (premiumStatus) {
      case 'active':
        return <span className="premium-badge premium-active">✓ Active</span>;
      case 'inactive':
        return <span className="premium-badge premium-inactive">License Inactive</span>;
      case 'none':
        return <span className="premium-badge premium-none">Not Linked</span>;
      default:
        return <span className="premium-badge">Loading...</span>;
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <form onSubmit={handleSubmit} className="settings-form">
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">Settings saved successfully!</div>}

        <div className="settings-section">
          <h2>Your Profile</h2>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              required
            />
          </div>
        </div>

        <div className="settings-section">
          <h2>Family Settings</h2>
          <div className="form-group">
            <label htmlFor="familyName">Family Name</label>
            <input
              type="text"
              id="familyName"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Enter family name"
              required
            />
          </div>
          
          {joinCode && (
            <div className="form-group">
              <label htmlFor="joinCode">Family Join Code</label>
              <p className="join-code-instructions">
                Share this code with your spouse so they can join your family.
              </p>
              {copySuccess && <div className="form-success" style={{ marginBottom: '8px' }}>Code copied to clipboard!</div>}
              <div className="join-code-display">
                <div className="code" onClick={copyJoinCode} style={{ cursor: 'pointer' }}>
                  {joinCode}
                </div>
                <button
                  type="button"
                  onClick={copyJoinCode}
                  className="button button-secondary button-small"
                  style={{ marginTop: '8px' }}
                >
                  Copy Code
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="button button-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Premium Section */}
      <div className="settings-section settings-premium">
        <div className="premium-header">
          <h2>Premium</h2>
          {renderPremiumBadge()}
        </div>
        
        {premiumStatus === 'active' ? (
          <div className="premium-active-info">
            <p>🎉 Your family has premium access! AI task assignment is enabled for all family members.</p>
            {currentLicenseCode && (
              <p className="license-display">
                License: <code>{currentLicenseCode.slice(0, 8)}...</code>
              </p>
            )}
          </div>
        ) : premiumStatus === 'inactive' ? (
          <div className="premium-inactive-info">
            <p>⚠️ Your license is no longer active. Please contact support or purchase a new license.</p>
          </div>
        ) : (
          <div className="premium-claim-section">
            <p>Enter your license code to activate premium for your entire family.</p>
            
            <form onSubmit={handleClaimLicense} className="license-form">
              {claimError && <div className="form-error">{claimError}</div>}
              {claimSuccess && <div className="form-success">Premium activated! 🎉</div>}
              
              <div className="form-group">
                <label htmlFor="licenseCode">License Code</label>
                <input
                  type="text"
                  id="licenseCode"
                  value={licenseCode}
                  onChange={(e) => setLicenseCode(e.target.value)}
                  placeholder="Enter your license code"
                />
              </div>
              
              <button
                type="submit"
                className="button button-primary"
                disabled={claimLoading}
              >
                {claimLoading ? 'Activating...' : 'Activate Premium'}
              </button>
            </form>
          </div>
        )}
      </div>

      <div className="settings-section settings-links">
        <h2>Support</h2>
        <a
          href="mailto:ops@withkana.com"
          className="settings-link"
        >
          Contact Support
        </a>
      </div>

      <div className="settings-section">
        <h2>Account</h2>
        <button
          onClick={handleLogout}
          className="button button-secondary settings-logout"
        >
          Log Out
        </button>
      </div>

      {/* Admin Section - only visible to admin users */}
      {isAdmin && (
        <div className="settings-section settings-admin">
          <h2>Admin</h2>
          <p>Access support tools and debugging features.</p>
          <button
            className="button button-secondary"
            onClick={() => navigate('/admin')}
          >
            Open Admin Panel
          </button>
        </div>
      )}

      <BottomNav user={user} userProfile={userProfile} />
    </div>
  );
}

export default SettingsPage;
